import { EventHandler } from "@cervval.js/client-html5-core/src/components/event.decorator";
import { PermissionString } from "@cervval.js/common-permissions/src/dtos.permissions";
import { ValueOrFunction } from "@cervval.js/common/src/tools/object.tools";
import { MenuHelper } from "./menu/menu.helper";
import { MenuDefinitions, MenuItemDefinition, MenuItemInsertion } from "./menu/menuDefinition";
import { MenuFactories } from "./menu/menuFactory";
import { BasePageName, Page } from "./navigation.page";

export interface PageDefinition<MenuUid extends string = string, PagePermissionString extends PermissionString = PermissionString> {
    importPromise: () => Promise<Page>;// Promise used to create the Page instance

    menuItem?: MenuItemDefinition;
    menus?: MenuItemInsertion<MenuUid>[];

    /**
     * To reach the page, user needs to have all permissions. If there is an array within permissions, user needs to have at least one of the permissions 
     * ex : permissions=["P1",["P2","P3"]] => hasPermission(permissions) = hasPermission("P1") && (hasPermission("P2") || hasPermission("P3"))
     */
    permissions?: ValueOrFunction<(PagePermissionString | PagePermissionString[])[]>;

    /**
     * To reach the page, modules need to be enabled in conf. If there is an array within permissions, at least one module need to be enabled 
     * ex : neededModuleNames=["M1",["M2","M3"]] => isActive = isActive("M1") && (isActive("M2") || isActive("M3"))
     */
    neededModuleNames?: (string | string[])[];
}

/**
 * PageDefinition by pageName
 */
export type PageDefinitions<PageName extends BasePageName = BasePageName, MenuUid extends string = string, PagePermissionString extends PermissionString = PermissionString> = Record<PageName, PageDefinition<MenuUid, PagePermissionString>>;

/** Url params */
export type SearchParams = { [key: string]: string };

/**
 * Parameters used to navigate / returned by listeners
 * TODO - Complete
 */
export interface NavigationParameters {

    /** Page where navigation action start (from) */
    previousPage: BasePageName;

    /** Page where navigation action end (to) */
    navigationPage: BasePageName;

    /**
     * Query parameters
     * Used to pass data from one page to anothers with parameters
     */
    queryParams?: SearchParams;
}

export const DEFAULT_NAVIGATION_CONTAINER_REF = "pagesContainer";// default ref of the container (prefer a <div>) where pages will be inserted and displayed

/**
 * Parameters to install the navigation service
 */
export interface NavigationServiceConfiguration<PageName extends BasePageName, PermissionContext = void> {

    /**
     * Default page for navigation
     */
    defaultPage: PageName;

    /**
     * Pages to register in navigationService
     */
    pages?: PageDefinitions<PageName>;

    /**
     * Default error page for navigation
     */
    defaultErrorPage?: PageName;

    /**
     * Optional: Custom container(prefer a <div>) ref where pages will be inserted and displayed (by default pagesContainer is used)
     */
    navigationContainerRef?: string;


    /**
     * MenuHelper manages the menus, if none provided, the default one will be used
     */
    menuHelper?: MenuHelper<PageName, PermissionContext>;

    /**
     * Menus to register in navigationService
     */
    menus?: MenuDefinitions;

    /**
     * Factories to create menu elements
     */
    menuFactories?: MenuFactories;

    /** Callback to get permissions when building pages or menues*/
    getterPermissionContext?: () => PermissionContext | undefined;

    /** Callback to get active modules when building pages or menues */
    getterActivatedModules?: () => Set<string>;
}

export const NAVIGATION_SERVICE = "NavigationService";
/**
 * NavigationService handles navigation between multiple pages in a single page application
 */
export interface NavigationService<PageName extends BasePageName, PermissionContext = void> {

    /**
     * Store current navigation page value
     * @description CurrentPage, is updated only when a page is displayed, this one becomes the currentPage
     */
    currentPage: PageName | undefined;

    /**
     * List of pages keys used for navigation service
     */
    pageNames: PageName[];

    /**
     * @param pageName to get menuItem of
     * @returns MenuItem of page if in pages and has one
     */
    getPageMenuItem(pageName: PageName): MenuItemDefinition | undefined;

    /**
     * Configure the navigation service
     */
    configure(params: NavigationServiceConfiguration<PageName, PermissionContext>): void;

    /**
     * Add pages to the navigation service
     * should be used at initialization of application
     *
     * @param pages - PagesWrapper to add to registered pages
     */
    addPages(pages: PageDefinitions<PageName>): void;

    /** 
     * Filters the pages or menus (depending on given @param elementTofilter) available for current user according to his permissions
     * Modifies the pages menues parameter and returns it
     */
    filterPagesOrMenusFromPermissions<T extends PageDefinitions | MenuDefinitions>(elementTofilter: T): T;

    /**
     * Navigate to pageName
     */
    navigate(pageName: PageName, params?: NavigationParameters): void;

    /**
     * @returns a url that allows navigation to the page with the provided parameters
     */
    getUrl(pageName: PageName, params?: NavigationParameters): string;

    /**
     * @returns search params of current url
     */
    getSearchParams(): SearchParams;

    /**
     * Delete a search params from current url
     */
    deleteSearchParams(...keys: string[]): void;

    /** 
     * Navigate to previous url location in history
     */
    navigatePrevious(): void;

    /**
     * Navigate to next url location in history
     */
    navigateNext(): void;

    /** 
     * Performs a navigation that hides current page and then shows current page
     */
    refreshCurrentPage(): void;

    /**
     * Convert navigationService attributes to string
     */
    toString(): string;

    /**
     * Convert navigationService attributes to json
     */
    toStringJSON(): string;

    /**
     * Clear all pages/menus of app then rebuild it from current user permission
     * @throws in case of incorrect configuration (no settings, no current page)
     */
    rebuildAppPagesFromPermissions(): void;

    /** Before page show event triggered before page lifecycle event */
    beforePageShow: EventHandler<{ pageName: PageName }>;

    /** After page show event triggered after page lifecycle event */
    afterPageShow: EventHandler<{ pageName: PageName }>;

    /** Before navigation event */
    beforeNavigation: EventHandler<{ previous: PageName | undefined, next: PageName }>;
}